import clsx from 'clsx';
import { useRef, useState } from 'react';

import { Button } from '@flintmint/web-shared/components/button';
import { Divider } from '@flintmint/web-shared/components/divider';

import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';
import { useAutosizeTextArea } from '@flintmint/web-shared/hooks/use-autosize-textarea';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';

import { toRem } from '@flintmint/web-shared/utils/ui';
import { appRoutes } from '@/constants/routes';

import type { Note } from '@flintmint/core-shared/rxdb';
import type { RxDocument } from 'rxdb';

import styles from './medical-note-prompt.module.scss';

export interface MedicalNotePromptProps {
  title: string;
  tagIds: string[];
  upsertNote: (payload: Partial<Note>) => Promise<RxDocument<Note>>;
}

export const MedicalNotePrompt = ({ title, tagIds, upsertNote }: MedicalNotePromptProps) => {
  const [shorthandNote, setShorthandNote] = useState<string>('');

  const ref = useAutosizeTextArea(shorthandNote);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const navigateWithSearch = useNavigateWithSearch();

  const { showToastNotification } = useToaster();

  const top = ref.current?.getBoundingClientRect().y ?? 0;

  return (
    <div className={styles.medicalNotePromptHolder}>
      <ul className={clsx(styles.medicalNotePrompt, 'with-gradient-border')}>
        <li>
          <p>Medical Note</p>
        </li>
        <li>
          <Divider orientation="horizontal" size="100%" />
          <textarea
            ref={ref}
            autoFocus
            value={shorthandNote}
            style={{
              minHeight: toRem(72),
              maxHeight: `calc(100vh - ${top + (buttonRef.current?.clientHeight ?? 0) + 12 + 36}px)`,
            }}
            onChange={(e) => setShorthandNote(e.target.value)}
            name="medical-note-prompt"
            placeholder="Provide the AI with the patient's name, date of birth, medical history, current medications, and any allergies. Additionally, include a brief summary of the patient's visit, outlining symptoms, diagnosis, and recommended treatment."
            data-testid="medical-note-prompt-input"
          />
        </li>
      </ul>
      <Button
        data-testid="generate-the-note-button"
        ref={buttonRef}
        disabled={!shorthandNote}
        onClick={async () => {
          try {
            const newNote = await upsertNote({
              variant: 'notes',
              tagIds,
              name: title || 'Untitled',
            });
            navigateWithSearch(`${appRoutes.library}/${newNote.id}`, {
              removeSearchParams: ['draftType', 'domain'],
              state: {
                shorthandNote,
              },
            });
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Something went wrong';
            showToastNotification({ type: 'error', title: message });
          }
        }}
      >
        Generate the Note
      </Button>
    </div>
  );
};
